import React, { useMemo, useState } from 'react';
import { colors } from './theme';
import { filterLifecyclePoints } from './mtbfUtils';
import LoadingSpinner from './components/LoadingSpinner';
import { analyticsCardStyles as card } from './analytics/analyticsCardStyles';

export default function MtbfLifecycleGraphCard({ points, failureCount }) {
  const [seriesFilter, setSeriesFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // available product series for filter options
  const seriesOptions = useMemo(() => {
    const opts = new Set();
    (Array.isArray(points) ? points : []).forEach(p => {
      if (p.series) opts.add(p.series);
    });
    return Array.from(opts).sort();
  }, [points]);

  // filter and normalise data
  const data = useMemo(() => {
    const arr = Array.isArray(points) ? points : [];
    const normalised = arr.map(p => ({ ...p, approvalDate: new Date(p.approvalDate) }));
    return filterLifecyclePoints(normalised, {
      series: seriesFilter,
      startDate,
      endDate,
    });
  }, [points, seriesFilter, startDate, endDate]);

  if (points === undefined || points === null) {
    return (
      <div
        style={{
          ...card.shell,
          maxWidth: 760,
        }}
      >
        <div style={card.header}>
          <div style={card.titleWrap}>
            <p style={card.eyebrow}>Lifecycle trend</p>
            <h2 style={card.title}>MTBF Lifecycle Graph</h2>
          </div>
        </div>
        <div style={card.body}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        style={{
          ...card.shell,
          maxWidth: 760,
        }}
      >
        <div style={card.header}>
          <div style={card.titleWrap}>
            <p style={card.eyebrow}>Lifecycle trend</p>
            <h2 style={card.title}>MTBF Lifecycle Graph</h2>
          </div>
        </div>
        <div style={card.body}>
          No warranty-accepted failures found.
        </div>
      </div>
    );
  }

  const failures = Number(failureCount) || 0;
  const width = 600;
  const height = 300;
  const margin = 40;
  const binSize = 30; // days per bin
  const maxDataX = Math.max(
    data.reduce((m, p) => Math.max(m, p.timeSinceDeploymentDays), 0),
    binSize
  );
  const futureDays = 5 * 365;
  const maxX = maxDataX + futureDays;
  const binCount = Math.ceil(maxDataX / binSize);
  const bins = Array.from({ length: binCount }, (_, i) => ({
    start: i * binSize,
    end: (i + 1) * binSize,
    count: 0,
  }));
  data.forEach(p => {
    const idx = Math.min(Math.floor(p.timeSinceDeploymentDays / binSize), binCount - 1);
    bins[idx].count++;
  });
  const maxY = bins.reduce((m, b) => Math.max(m, b.count), 0) || 1;

  const xScale = x => margin + (x / maxX) * (width - margin * 2);
  const yScale = y => height - margin - (y / maxY) * (height - margin * 2);

  const pathD = bins
    .map((b, i) => {
      const x = xScale(b.start + binSize / 2);
      const y = yScale(b.count);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');

  // linear regression for trend line
  const n = bins.length;
  const sumX = bins.reduce((sum, b) => sum + (b.start + binSize / 2), 0);
  const sumY = bins.reduce((sum, b) => sum + b.count, 0);
  const sumXY = bins.reduce(
    (sum, b) => sum + (b.start + binSize / 2) * b.count,
    0
  );
  const sumXX = bins.reduce(
    (sum, b) => sum + (b.start + binSize / 2) * (b.start + binSize / 2),
    0
  );
  const denom = n * sumXX - sumX * sumX;
  const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
  const intercept = n ? (sumY - slope * sumX) / n : 0;
  const trend = x => slope * x + intercept;

  const trendActual = `M${xScale(0)},${yScale(trend(0))} L${xScale(
    maxDataX
  )},${yScale(trend(maxDataX))}`;
  const trendPredicted = `M${xScale(maxDataX)},${yScale(
    trend(maxDataX)
  )} L${xScale(maxDataX + futureDays)},${yScale(trend(maxDataX + futureDays))}`;

  return (
    <div
      style={{
        ...card.shell,
        maxWidth: width + 120,
      }}
    >
      <div style={card.header}>
        <div style={card.titleWrap}>
          <p style={card.eyebrow}>Lifecycle trend</p>
          <h2 style={card.title}>MTBF Lifecycle Graph</h2>
        </div>
      </div>
      <div style={card.body}>
        <p style={{ marginTop: 0, color: colors.textMuted }}>
          This graph shows when devices fail during their lifecycle.
        </p>
        {/* filters */}
        <div
          style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}
        >
          <label style={{ color: colors.textBody }}>
            Series:
            <select className="ni-select" value={seriesFilter} onChange={e => setSeriesFilter(e.target.value)} style={{ marginLeft: '0.25rem' }}>
              <option value="all">All</option>
              {seriesOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label style={{ color: colors.textBody }}>
            Start:
            <input className="ni-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ marginLeft: '0.25rem', width: 'auto' }} />
          </label>
          <label style={{ color: colors.textBody }}>
            End:
            <input className="ni-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ marginLeft: '0.25rem', width: 'auto' }} />
          </label>
        </div>
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ background: colors.surfaceMuted, borderRadius: '16px' }}
        >
          {/* axes */}
          <line
            x1={margin}
            y1={height - margin}
            x2={width - margin}
            y2={height - margin}
            stroke={colors.text}
            strokeWidth={2}
          />
          <line
            x1={margin}
            y1={height - margin}
            x2={margin}
            y2={margin / 2}
            stroke={colors.text}
            strokeWidth={2}
          />
          {/* axis labels */}
          <text
            x={width / 2}
            y={height - 5}
            textAnchor="middle"
            fill={colors.text}
            fontSize={12}
          >
            Time Since Deployment (Days)
          </text>
          <text
            x={-height / 2}
            y={15}
            transform="rotate(-90)"
            textAnchor="middle"
            fill={colors.text}
            fontSize={12}
          >
            Failure Count
          </text>
          {/* histogram bars */}
          {bins.map(b => (
            <rect
              key={b.start}
              x={xScale(b.start)}
              y={yScale(b.count)}
              width={xScale(b.end) - xScale(b.start) - 1}
              height={height - margin - yScale(b.count)}
              fill={colors.accent}
              opacity={0.3}
            />
          ))}
          {/* trend line */}
          <path d={pathD} stroke={colors.accent} fill="none" strokeWidth={3} />
          <path d={trendActual} stroke={colors.text} fill="none" strokeWidth={2} />
          <path
            d={trendPredicted}
            stroke={colors.text}
            fill="none"
            strokeWidth={2}
            strokeDasharray="4 4"
          />
          <text
            x={xScale(maxDataX + futureDays) - 5}
            y={yScale(trend(maxDataX + futureDays)) - 5}
            textAnchor="end"
            fill={colors.text}
            fontSize={10}
          >
            Predicted trend for next 5 years
          </text>
          {/* individual points for tooltip */}
          {data.map(p => (
            <circle
              key={p.serialNumber + p.approvalDate}
              cx={xScale(p.timeSinceDeploymentDays)}
              cy={yScale(
                bins[
                  Math.min(
                    Math.floor(p.timeSinceDeploymentDays / binSize),
                    binCount - 1
                  )
                ].count
              )}
              r={5}
              fill={colors.accent}
              stroke={colors.text}
              strokeWidth={1}
            >
              <title>{`Serial Number: ${p.serialNumber}\nProduct Number: ${p.productNumber}\nProduct Series: ${p.series}\nRMA Approval Date: ${p.approvalDate.toLocaleDateString()}\nTime Since Deployment: ${p.timeSinceDeploymentDays} days`}</title>
            </circle>
          ))}
        </svg>
        <div style={{ marginTop: '1rem', fontFamily: 'monospace' }}>
          <div>
            <strong>All-Time Warranty Accepted by NIN:</strong> {failures}
          </div>
        </div>
      </div>
    </div>
  );
}
